/**
 * Loading Spinner Component
 *
 * Displays a loading indicator with optional full-screen mode.
 */

import React from 'react';

/**
 * LoadingSpinner component
 * @param {Object} props - Component props
 * @param {boolean} props.fullScreen - Show spinner full screen
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg'
 * @param {string} props.message - Optional loading message
 * @param {string} props.className - Additional CSS classes
 */
function LoadingSpinner({
  fullScreen = false,
  size = 'md',
  message = '',
  className = '',
}) {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-8 w-8 border-2',
    lg: 'h-12 w-12 border-3',
    xl: 'h-16 w-16 border-4',
  };

  const spinnerElement = (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <div
        className={`
          ${sizeClasses[size] || sizeClasses.md}
          rounded-full
          border-gray-200
          border-t-blue-600
          animate-spin
        `}
        role="status"
        aria-label="Loading"
      />
      {message && (
        <p className="mt-3 text-sm text-gray-600 animate-pulse">
          {message}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-90 z-50">
        {spinnerElement}
      </div>
    );
  }

  return spinnerElement;
}

/**
 * Skeleton loader for content placeholders
 */
export function Skeleton({
  width = 'full',
  height = '4',
  rounded = 'md',
  className = '',
}) {
  return (
    <div
      className={`
        animate-pulse bg-gray-200
        w-${width} h-${height} rounded-${rounded}
        ${className}
      `}
    />
  );
}

/**
 * Card skeleton for loading card content
 */
export function CardSkeleton() {
  return (
    <div className="card p-6 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4" />
      <div className="h-3 bg-gray-200 rounded w-1/2 mb-2" />
      <div className="h-3 bg-gray-200 rounded w-5/6 mb-2" />
      <div className="h-3 bg-gray-200 rounded w-2/3" />
    </div>
  );
}

/**
 * Table skeleton for loading table content
 */
export function TableSkeleton({ rows = 5, cols = 4 }) {
  return (
    <div className="animate-pulse">
      {/* Header */}
      <div className="flex border-b border-gray-200 pb-3 mb-3">
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="flex-1 h-4 bg-gray-200 rounded mx-2" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex py-3 border-b border-gray-100">
          {Array.from({ length: cols }).map((_, colIndex) => (
            <div
              key={colIndex}
              className="flex-1 h-3 bg-gray-100 rounded mx-2"
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Form skeleton for loading form content
 */
export function FormSkeleton({ fields = 4 }) {
  return (
    <div className="animate-pulse space-y-6">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i}>
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
          <div className="h-10 bg-gray-100 rounded w-full" />
        </div>
      ))}
      <div className="flex justify-end gap-3 pt-4">
        <div className="h-10 bg-gray-200 rounded w-24" />
        <div className="h-10 bg-gray-200 rounded w-24" />
      </div>
    </div>
  );
}

export default LoadingSpinner;
